import React, { FC } from "react";
import { AppPage } from "shared-ts-ui-app-page";
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  Stack,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { BurnAssessmentLegend, BurnsLoader } from "firefly-my-chimney-ts-burn";
import { useTimeframe } from "shared-ts-ui-timeframe";
import { BurnsPageContent } from "./BurnsPageContent";
import { useTranslation } from "react-i18next";
import { useDateTimeNow } from "shared-ts-utils-luxon";
import {
  SizeClass,
  useResponsive,
  useWindowWidth,
} from "shared-ts-ui-responsive";
import { DurationDisplay } from "shared-ts-ui-time";
import ExpandRoundedIcon from "@mui/icons-material/ExpandRounded";
import { TimeAxisMaxDuration } from "./TimeAxisMaxDuration";
import { defaultTimeAxisDuration } from "./defaultTimeAxisDuration";
import { Duration } from "luxon";
import {
  BooleanSetting,
  NonNullableSettingConfig,
  SettingValueType,
  useUserSetting,
  useUserSettingWithDefault,
} from "shared-ts-utils-user-settings";
import { createLazyRoute } from "@tanstack/react-router";
import { useMyChimneyContext } from "my-chimney-ts-my-chimney-context";
import { MascotGDPR } from "firefly-my-chimney-ts-shared";
import { If } from "shared-ts-ui-react";
import { _throw, IllegalStateError } from "shared-ts-utils-core";

class TimeAxisMaxDurationSetting
  implements NonNullableSettingConfig<TimeAxisMaxDuration>
{
  static HalfHourDuration = Duration.fromDurationLike({ minute: 30 });
  type: SettingValueType;

  constructor() {
    this.type = "json";
  }

  serialize = (value: TimeAxisMaxDuration): string => {
    if (value instanceof Duration) {
      return (
        value.toISO() ??
        _throw(
          new IllegalStateError(
            "Unable serialize value: " + JSON.stringify(value)
          )
        )
      );
    }
    return value;
  };
  deserialize = (value: string): TimeAxisMaxDuration => {
    if (value === "PT30M") {
      return TimeAxisMaxDurationSetting.HalfHourDuration;
    }
    if (value === "default" || value === "all") {
      return value;
    }
    return "default";
  };
}

export const BurnsPage: FC = (props) => {
  const { t } = useTranslation("my-chimney-ts-translations");
  const theme = useTheme();
  const responsive = useResponsive();
  const now = useDateTimeNow();
  const { selectedBuilding, selectedFlue } = useMyChimneyContext();
  const [timeAxisMaxDuration, setTimeAxisMaxDuration] =
    useUserSettingWithDefault<TimeAxisMaxDuration>(
      "BurnsPage.timeAxisMaxDuration",
      new TimeAxisMaxDurationSetting(),
      "default"
    );
  const [
    displayMaxTemperatureReferenceLines,
    setDisplayMaxTemperatureReferenceLines,
  ] = useUserSettingWithDefault(
    "BurnsPage.displayAvgMaxTemperatureReferenceLines",
    BooleanSetting,
    false
  );
  const layoutWidth = useWindowWidth();
  const timeframeProps = useTimeframe({
    disallowFuture: true,
    maxEnd: now.startOf("day"),
    allowedUnits: ["month"],
    persistUsingUserSettings: true,
    storageId: () => "BurnsPage.timeframe",
  });

  const [consentBurnTimeIsSaved] = useUserSetting(
    "consentBurnTimeIsSaved",
    BooleanSetting
  );

  const [
    displayConsentBurnTimeIsNotSavedMessage,
    setDisplayConsentBurnTimeIsNotSavedMessage,
  ] = useUserSettingWithDefault(
    "displayConsentBurnTimeIsNotSavedMessage",
    BooleanSetting,
    true
  );
  const handleCloseConsentBurnTimeIsNotSavedMessage = () => {
    setDisplayConsentBurnTimeIsNotSavedMessage(false);
  };

  return (
    <AppPage
      $key={"BurnsPage"}
      name={t("Burns")}
      hideName
      mainPaneProps={{
        scrollableContentVertically: true,
      }}
      mainPane={
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
          }}
        >
          <If
            expression={selectedFlue.fields.shared === true}
            then={
              <Stack useFlexGap gap={4} sx={{ alignItems: "center" }}>
                <MascotGDPR
                  mode={theme.palette.mode}
                  style={{ maxWidth: "250px" }}
                />
                <Typography align={"center"}>
                  {t("FlueSharedPrivacy")}
                </Typography>
              </Stack>
            }
            else={
              <BurnsLoader
                environment={selectedBuilding.environment.id}
                flueId={selectedFlue.id}
                fromDate={timeframeProps.timeframe.start}
                toDate={timeframeProps.timeframe.end}
              >
                {(result) => {
                  return (
                    <BurnsPageContent
                      layout={layoutWidth}
                      timeframeProps={timeframeProps}
                      burnsLoadingResult={result}
                      timeAxisDomain={{
                        maxDuration: timeAxisMaxDuration,
                      }}
                      hideMaxTemperatureReferenceLines={
                        !displayMaxTemperatureReferenceLines
                      }
                    />
                  );
                }}
              </BurnsLoader>
            }
          />
        </Box>
      }
      supportingPaneProps={{
        label: t("More"),
        opacity: 0.3,
        scrollableContentVertically: true,
        resizePanelProps: {
          minWidth: 320,
          maxWidth: 400,
          vertical: {
            maxHeight: 300,
          },
          mobile: {
            maxHeight: 300,
          },
        },
      }}
      supportingPane={({ toolbarContainer, layout }) => {
        return (
          <Card
            sx={{
              flexGrow: 1,
              p: 2,
            }}
          >
            <Stack
              useFlexGap
              gap={1}
              sx={{
                flexGrow: 1,
                minHeight: 0,
                minWidth: 0,
                alignItems: "stretch",
              }}
            >
              <Toolbar
                variant={responsive.toolbar.variant}
                sx={{
                  flexDirection: "row",
                  gap: 1,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography>{t("Duration of the time axis")}:</Typography>
                <ToggleButtonGroup
                  size={responsive.component.size}
                  value={timeAxisMaxDuration}
                  exclusive
                  onChange={(_, value) => setTimeAxisMaxDuration(value)}
                >
                  <ToggleButton
                    value={TimeAxisMaxDurationSetting.HalfHourDuration}
                  >
                    <DurationDisplay
                      layout={SizeClass.Compact}
                      duration={TimeAxisMaxDurationSetting.HalfHourDuration}
                      maxUnits={1}
                    />
                  </ToggleButton>
                  <ToggleButton value={"default"}>
                    <DurationDisplay
                      layout={SizeClass.Compact}
                      duration={defaultTimeAxisDuration}
                      maxUnits={1}
                    />
                  </ToggleButton>
                  <ToggleButton value={"all"}>
                    <ExpandRoundedIcon sx={{ transform: "rotate(90deg)" }} />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Toolbar>
              <Toolbar
                variant={responsive.toolbar.variant}
                sx={{
                  flexDirection: "row",
                  gap: 1,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography>
                  {t("Display Max Temperatures:breakable")}:
                </Typography>
                <Switch
                  value={displayMaxTemperatureReferenceLines}
                  onChange={(event, value) =>
                    setDisplayMaxTemperatureReferenceLines(value)
                  }
                />
              </Toolbar>
              <Toolbar
                variant={responsive.toolbar.variant}
                sx={{
                  flexDirection: "row",
                  gap: 1,
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <BurnAssessmentLegend layout={layoutWidth} size={"default"} />
              </Toolbar>
              {!consentBurnTimeIsSaved &&
                displayConsentBurnTimeIsNotSavedMessage && (
                  <Alert
                    severity={"info"}
                    onClose={handleCloseConsentBurnTimeIsNotSavedMessage}
                  >
                    <AlertTitle>
                      {t("The time of your burns are anonymized")}
                    </AlertTitle>
                    {t(
                      "Go to your user settings to turn off anonymization of your burns, if you wish"
                    )}
                  </Alert>
                )}
            </Stack>
          </Card>
        );
      }}
    />
  );
};

export const BurnsPageRoute = createLazyRoute("/_myChimneyContext/burns")({
  component: BurnsPage,
});
